import { useCallback, useEffect, useRef, useState } from "react";
import { StatusIcon } from "../ui/statusIcon.tsx";
import { Button } from "../ui/button.tsx";
import { AnnouncementModal } from "./announcementModal.tsx";
import { getMyAnnouncements, markAnnouncementRead, type Announcement } from "../../api/announcements.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { useToast } from "../../hooks/useToast.ts";
import styles from "./announcement.module.scss";

const COLLAPSED_KEY = "announcementBannerCollapsed";

/** Which announcement the user folded away, and in which login: "{Session}:{AnnouncementId}". A new login or a new announcement shows the banner open again. */
function readCollapsed(): string | null {
  try { return sessionStorage.getItem(COLLAPSED_KEY); } catch { return null; }
}

function writeCollapsed(value: string | null): void {
  try {
    if (value) sessionStorage.setItem(COLLAPSED_KEY, value);
    else sessionStorage.removeItem(COLLAPSED_KEY);
  } catch { /* storage unavailable: the banner just opens again next time */ }
}

/**
 * The announcement at the top of Cockpit. Shows the newest announcement the signed-in user has not marked as read;
 * "Read more" opens the full text, and "Mark as read" there writes the read receipt (a SuperAdmin sees who has read
 * what in the Announcements tool). They are the existing global announcements, fetched once per load.
 * It always opens expanded on a login, but "Hide" folds it into a one-line strip for the rest of that login (reloads
 * included), without marking it as read; a new login brings it back until it has been marked as read.
 * A failed fetch shows nothing: a banner is never worth a broken page.
 */
export function AnnouncementBanner() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [items, setItems] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [collapsedKey, setCollapsedKey] = useState<string | null>(readCollapsed);

  // StrictMode double-mount: one fetch (bootedRef, so no `cancelled` flag — see AGENTS.md).
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    getMyAnnouncements()
      .then((r) => { if (!r.bError && Array.isArray(r.oData)) setItems(r.oData); })
      .catch(() => { /* no banner */ });
  }, []);

  const current = items[0] ?? null;

  const handleMarkRead = useCallback(async () => {
    if (!current) return;
    setMarking(true);
    try {
      const r = await markAnnouncementRead(current.AnnouncementId);
      if (r.bError) {
        toast(r.sMessage || "Could not save that you have read it. Try again.", "error");
        return;
      }
      setItems((prev) => prev.filter((a) => a.AnnouncementId !== current.AnnouncementId));
      setOpen(false);
      toast("Marked as read", "success");
    } catch {
      toast("Could not save that you have read it. Try again.", "error");
    } finally {
      setMarking(false);
    }
  }, [current, toast]);

  if (!current) return null;

  const key = `${session?.Session ?? ""}:${current.AnnouncementId}`;
  const collapsed = collapsedKey === key;
  const setCollapsed = (next: boolean) => {
    const value = next ? key : null;
    writeCollapsed(value);
    setCollapsedKey(value);
  };

  if (collapsed) {
    return (
      <button
        type="button"
        className={styles.strip}
        onClick={() => setCollapsed(false)}
        aria-expanded={false}
        aria-label={`Show news: ${current.Title}`}
      >
        <StatusIcon kind="info" size={14} style={{ color: "var(--color-primary)" }} />
        <span className={styles.stripEyebrow}>News</span>
        <span className={styles.stripTitle}>{current.Title}</span>
        {items.length > 1 && <span className={styles.count}>1 of {items.length}</span>}
        <span className={styles.stripShow}>Show <StatusIcon kind="chevronDown" size={14} /></span>
      </button>
    );
  }

  return (
    <>
      <section className={styles.banner} aria-label={`News: ${current.Title}`}>
        <span className={styles.icon} aria-hidden="true">
          <StatusIcon kind="info" size={20} style={{ color: "var(--color-primary)" }} />
        </span>
        <div className={styles.text}>
          <span className={styles.eyebrow}>News</span>
          <strong className={styles.title}>{current.Title}</strong>
          <span className={styles.preview}>{current.Body}</span>
        </div>
        <div className={styles.actions}>
          {current.AttachmentName && (
            <span className={styles.attachment}><StatusIcon kind="file" size={14} /> PDF</span>
          )}
          {items.length > 1 && <span className={styles.count}>1 of {items.length}</span>}
          <Button variant="primary" size="sm" onClick={() => setOpen(true)}>Read more</Button>
          <Button variant="ghost" size="sm" onClick={() => setCollapsed(true)} aria-expanded={true} title="Hide until your next login, or until you mark it as read">
            Hide <StatusIcon kind="chevronDown" size={14} style={{ transform: "rotate(180deg)" }} />
          </Button>
        </div>
      </section>
      <AnnouncementModal
        announcement={open ? current : null}
        onClose={() => { if (!marking) setOpen(false); }}
        onMarkRead={handleMarkRead}
        marking={marking}
      />
    </>
  );
}
